import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { siteConfig, categories } from '@/mocks/categories';
import { getAllArticleSlugs, getArticleBySlug } from '@/mocks/articles';

const siteUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) || siteConfig.domain;

const homeJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'SuperBuySheet',
  url: siteUrl,
  description: 'The ultimate independent Superbuy Spreadsheet resource. Discover updated product databases, curated finds, expert shopping guides, QC spreadsheets, and beginner-friendly tutorials for smart international shoppers.',
  inLanguage: 'en',
  publisher: {
    '@type': 'Organization',
    name: 'SuperBuySheet',
    url: siteUrl,
  },
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: siteUrl + '/article/{search_term_string}',
    },
    'query-input': 'required name=search_term_string',
  },
};

function CategoryDropdown() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative w-full max-w-md mx-auto">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-5 py-3.5 bg-background-50 border border-background-300/60 rounded-xl text-foreground-800 hover:border-primary-300 transition-all duration-200 cursor-pointer group"
      >
        <span className="text-sm font-medium">Browse Categories</span>
        <i className={`ri-arrow-down-s-line text-lg transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}></i>
      </button>

      {isOpen && (
        <div className="absolute top-full mt-2 left-0 right-0 bg-background-50 border border-background-200/70 rounded-xl z-40 max-h-72 overflow-y-auto">
          {categories.map((cat) => (
            <a
              key={cat.seq}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-3 px-5 py-3 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-all duration-150 cursor-pointer first:rounded-t-xl last:rounded-b-xl whitespace-nowrap"
            >
              <div className="w-7 h-7 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                <i className={`${cat.icon} text-sm`}></i>
              </div>
              {cat.name}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function CategoryCards() {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 md:gap-4">
      {categories.map((cat) => (
        <a
          key={cat.seq}
          href={cat.url}
          target="_blank"
          rel="noopener nofollow"
          className="group flex flex-col items-center gap-2 p-4 bg-background-50 border border-background-200/70 rounded-xl hover:border-primary-200 hover:bg-primary-50/30 transition-all duration-200 cursor-pointer"
        >
          <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600 group-hover:bg-primary-100 group-hover:text-primary-600 transition-all duration-200">
            <i className={`${cat.icon} text-xl`}></i>
          </div>
          <span className="text-xs font-medium text-foreground-700 group-hover:text-foreground-950 text-center whitespace-nowrap transition-colors duration-200">
            {cat.name}
          </span>
        </a>
      ))}
    </div>
  );
}

function SEOGuidesSection() {
  const allSlugs = getAllArticleSlugs();
  const guideArticles = allSlugs.map((s) => getArticleBySlug(s)).filter((a) => a !== undefined);

  return (
    <section className="py-16 md:py-20 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
            <i className="ri-article-line"></i>
            Expert Guides
          </div>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
            Superbuy Spreadsheet Knowledge Base
          </h2>
          <p className="text-foreground-600 max-w-xl mx-auto text-sm md:text-base">
            In-depth guides covering every aspect of spreadsheet shopping — from beginner basics to advanced category strategies.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {guideArticles.map((article) => (
            <Link
              key={article.slug}
              to={`/article/${article.slug}`}
              className="group flex flex-col p-5 bg-background-100 border border-background-200/70 rounded-xl hover:border-primary-200 hover:bg-primary-50/20 transition-all duration-200"
            >
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                  <i className="ri-article-line"></i>
                </div>
                <span className="text-xs font-medium text-accent-700 whitespace-nowrap">{article.badge}</span>
              </div>
              <h3 className="font-heading text-sm font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                {article.title}
              </h3>
              <p className="text-xs text-foreground-600 leading-relaxed line-clamp-2 mb-3 flex-grow">
                {article.description}
              </p>
              <span className="flex items-center gap-1 text-xs font-medium text-primary-600 whitespace-nowrap">
                Read Guide
                <i className="ri-arrow-right-line text-xs"></i>
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Navbar />
      <main>
        {/* JSON-LD Structured Data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(homeJsonLd) }}
        />

        {/* Hero Section */}
        <section className="relative w-full min-h-[580px] md:min-h-[680px] flex items-center justify-center overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(https://readdy.ai/api/search-image?query=Bold%20fashion%20editorial%20composition%20with%20dark%20charcoal%20grey%20concrete%20backdrop%2C%20vibrant%20olive%20green%20silk%20scarf%20draped%20over%20crisp%20white%20cotton%20shirt%2C%20rust%20orange%20leather%20belt%20coiled%20beside%20brushed%20metal%20watch%2C%20dramatic%20side%20lighting%20from%20left%20creating%20deep%20angular%20shadows%2C%20high%20contrast%20editorial%20photography%20style%2C%20scattered%20olive%20branches%20for%20organic%20texture%2C%20luxury%20commercial%20fashion%20aesthetic%2C%20sophisticated%20minimalist%20product%20photography%20with%20bold%20color%20pops&width=1800&height=900&seq=hero-superbuy-v2-2026&orientation=landscape)`,
            }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/40"></div>

          <div className="relative z-10 w-full px-4 md:px-6 text-center max-w-4xl mx-auto">
            <p className="text-sm md:text-base font-medium tracking-widest uppercase text-white/80 mb-4">
              Your Gateway to Smarter Shopping
            </p>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Superbuy Spreadsheet
              <br />
              <span className="text-white/90 font-normal text-2xl md:text-4xl lg:text-5xl">
                The Ultimate Resource for Finding Products, Deals &amp; Shopping Guides
              </span>
            </h1>
            <p className="text-base md:text-lg text-white/70 max-w-2xl mx-auto mb-10 leading-relaxed">
              Discover updated Superbuy Spreadsheet resources, product databases, comparison guides and expert shopping tutorials — all curated for smart global shoppers.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href={siteConfig.mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="w-full sm:w-auto px-8 py-3.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold text-sm rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                Superbuy Sheet
              </a>
              <a
                href={siteConfig.mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="w-full sm:w-auto px-8 py-3.5 bg-white/15 hover:bg-white/25 backdrop-blur-sm text-white font-semibold text-sm border border-white/30 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </section>

        {/* Trust Bar */}
        <section className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-5">
            <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
              {['Updated Resources', 'Independent Platform', 'Beginner Friendly', 'Global Shopper Community'].map(
                (text, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-foreground-600">
                    <div className="w-5 h-5 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className="ri-check-line text-xs"></i>
                    </div>
                    {text}
                  </div>
                )
              )}
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section id="categories" className="py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
                Explore Product Categories
              </h2>
              <p className="text-foreground-600 max-w-xl mx-auto text-sm md:text-base">
                Browse through our curated categories and discover the best products from KASpreadsheet.
              </p>
            </div>

            <div className="mb-8">
              <CategoryDropdown />
            </div>

            <CategoryCards />
          </div>
        </section>

        {/* SEO Guides Section */}
        <SEOGuidesSection />

        {/* About Section with SEO Content */}
        <section id="about" className="py-16 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6 text-center">
              What Is Superbuy Spreadsheet?
            </h2>

            <div className="space-y-5 text-foreground-700 leading-relaxed text-sm md:text-base">
              <p>
                A <strong>Superbuy Spreadsheet</strong> is an organized, community-curated collection of product links, quality inspection notes, pricing comparisons, and shopping recommendations designed specifically for international shoppers using the Superbuy agent platform. Instead of spending hours manually searching through endless product listings, savvy shoppers rely on these spreadsheets to quickly identify trending items, verified sellers, and the best value-for-money finds across multiple categories including shoes, clothing, accessories, and lifestyle goods.
              </p>

              <p>
                What sets the <strong>Superbuy Spreadsheet</strong> apart from regular browsing is the layer of human verification built into every entry. Each item typically includes real QC (Quality Check) photos, actual weight measurements, shipping estimations, and community feedback — transforming a simple product list into a powerful research tool. Whether you are hunting for the latest sneaker releases, budget-friendly streetwear pieces, or unique accessories that are difficult to find elsewhere, having access to a well-maintained spreadsheet can save you both time and money while reducing the risk of disappointing purchases.
              </p>

              <p>
                The SuperBuySheet platform serves as a gateway to the most comprehensive <strong>Superbuy Spreadsheet</strong> resources available online. By connecting shoppers directly to the KASpreadsheet ecosystem, we make it effortless to access categorized product databases, updated buying guides, and expert shopping strategies — all in one place.
              </p>
            </div>

            {/* Why Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mt-12">
              {[
                {
                  icon: 'ri-folder-line',
                  title: 'Organized Research',
                  desc: 'Products neatly categorized by type, style, and popularity for effortless browsing.',
                },
                {
                  icon: 'ri-time-line',
                  title: 'Save Time',
                  desc: 'Skip the endless scrolling — find what you need in seconds with curated collections.',
                },
                {
                  icon: 'ri-scales-line',
                  title: 'Compare Products',
                  desc: 'Side-by-side comparisons help you evaluate price, quality, and seller reliability.',
                },
                {
                  icon: 'ri-line-chart-line',
                  title: 'Discover Trends',
                  desc: 'Stay ahead of the curve with regularly updated lists featuring trending items.',
                },
              ].map((item, idx) => (
                <div
                  key={idx}
                  className="p-5 bg-background-50 border border-background-200/70 rounded-xl hover:border-primary-200 transition-all duration-200"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4">
                    <i className={`${item.icon} text-lg`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2">{item.title}</h3>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Comparison Section */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8 text-center">
              Superbuy Spreadsheet vs Traditional Searching
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse bg-white border border-background-200/70 rounded-xl overflow-hidden">
                <thead>
                  <tr className="bg-background-100">
                    <th className="text-left px-5 py-4 text-sm font-semibold text-foreground-950">Feature</th>
                    <th className="text-left px-5 py-4 text-sm font-semibold text-foreground-700">Traditional Search</th>
                    <th className="text-left px-5 py-4 text-sm font-semibold text-primary-700">Superbuy Spreadsheet</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Product Discovery', 'Manual, time-consuming', 'Curated, instant access'],
                    ['Quality Verification', 'No QC photos available', 'Real QC photos included'],
                    ['Price Comparison', 'One item at a time', 'Side-by-side comparisons'],
                    ['Community Feedback', 'Scattered across forums', 'Consolidated in one place'],
                    ['Update Frequency', 'N/A', 'Regularly updated'],
                    ['Learning Curve', 'Steep for beginners', 'Beginner-friendly layout'],
                  ].map((row, i) => (
                    <tr key={i} className="border-t border-background-200/70">
                      <td className="px-5 py-3.5 text-sm font-medium text-foreground-950">{row[0]}</td>
                      <td className="px-5 py-3.5 text-sm text-foreground-600">{row[1]}</td>
                      <td className="px-5 py-3.5 text-sm text-primary-700 font-medium">{row[2]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Article CTA Section */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-6">
              <i className="ri-book-open-line"></i>
              Deep Dive Guides
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
              Want to Master Superbuy Shopping?
            </h2>
            <p className="text-foreground-600 max-w-lg mx-auto mb-8 text-sm md:text-base">
              Explore our comprehensive guides covering everything from beginner basics to category-specific strategies, safety tips, and advanced shopping techniques.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/article/superbuy-spreadsheet-guide"
                className="inline-flex items-center gap-2 px-7 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold text-sm rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                Read the Complete Guide
                <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/article/superbuy-spreadsheet-for-beginners"
                className="inline-flex items-center gap-2 px-7 py-3 bg-background-50 hover:bg-background-200 text-foreground-700 font-semibold text-sm rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap border border-background-300/60"
              >
                Beginner Tutorial
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-20 md:py-24 overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(https://readdy.ai/api/search-image?query=Atmospheric%20nighttime%20luxury%20lifestyle%20scene%20with%20warm%20amber%20and%20golden%20hour%20sunset%20light%20casting%20soft%20glow%20across%20smooth%20slate%20grey%20marble%20surface%2C%20deep%20burgundy%20silk%20fabric%20elegantly%20draped%20beside%20polished%20brass%20accessories%2C%20candlelight%20reflections%20creating%20intimate%20ambiance%2C%20shallow%20depth%20of%20field%20with%20selective%20focus%20on%20foreground%20textures%2C%20luxury%20editorial%20mood%2C%20warm%20highlights%20against%20deep%20shadows%2C%20sophisticated%20premium%20commercial%20photography%20with%20rich%20tonal%20contrast&width=1800&height=700&seq=cta-superbuy-v2-2026&orientation=landscape)`,
            }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-black/25 to-black/40"></div>

          <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-white mb-4">
              Ready to Start Shopping Smarter?
            </h2>
            <p className="text-white/75 text-sm md:text-base mb-8 max-w-lg mx-auto">
              Join thousands of global shoppers who use KASpreadsheet to discover the best products at the best prices.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href={siteConfig.mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="w-full sm:w-auto px-8 py-3.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold text-sm rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                Superbuy Sheet
              </a>
              <a
                href={siteConfig.mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="w-full sm:w-auto px-8 py-3.5 bg-white/15 hover:bg-white/25 backdrop-blur-sm text-white font-semibold text-sm border border-white/30 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </section>

        {/* Disclaimer */}
        <section className="py-8 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="bg-secondary-50/50 border border-secondary-200/50 rounded-xl p-5 text-center">
              <p className="text-xs md:text-sm text-foreground-500">
                <strong className="text-foreground-700">Disclaimer:</strong> {siteConfig.disclaimer}
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}